import {
  // IconArrowBarToDown,
  // IconArrowBarUp,
  // IconBoxMultiple,
  IconChevronLeft,
  IconChevronRight,
  IconPackage,
  IconSearch,
} from "@tabler/icons-react";
import { Select, TextInput } from "@mantine/core";
import {
  buildStockGoods,
  sortStockGoods,
  type StockGoods,
} from "@/utility/stockHistory";
import { useEffect, useMemo, useState } from "react";

import StockMovementDrawer from "@/components/StockMovementDrawer";
import { orpcClient } from "@/lib/orpc-client";
import { useTranslation } from "react-i18next";

const PAGE_SIZE_OPTIONS = ["20", "50", "100"];
const DEFAULT_PAGE_SIZE = 20;

function StockTag({
  value,
  lowerLimit,
}: {
  value: number;
  lowerLimit: number | null;
}) {
  const { t } = useTranslation();
  const isOut = value <= 0;
  const isLow = !isOut && lowerLimit != null && value < lowerLimit;

  const tone = isOut
    ? "bg-red-50 text-red-600"
    : isLow
      ? "bg-amber-50 text-amber-600"
      : "bg-emerald-50 text-emerald-600";

  const label = isOut
    ? t("stockHistory.statusOut") || "缺貨"
    : isLow
      ? t("stockHistory.statusLow") || "低庫存"
      : t("stockHistory.statusNormal") || "正常";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

// function SummaryCard({
//   icon,
//   label,
//   value,
//   tone,
// }: {
//   icon: React.ReactNode;
//   label: string;
//   value: number;
//   tone: "primary" | "success" | "warning";
// }) {
//   const toneClass =
//     tone === "success"
//       ? "bg-emerald-50 text-emerald-600"
//       : tone === "warning"
//         ? "bg-amber-50 text-amber-600"
//         : "bg-blue-50 text-blue-600";

//   return (
//     <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
//       <span
//         className={`flex h-11 w-11 items-center justify-center rounded-lg ${toneClass}`}
//       >
//         {icon}
//       </span>
//       <div>
//         <p className="text-xs text-gray-500">{label}</p>
//         <p className="text-xl font-semibold tabular-nums text-gray-800">
//           {value.toLocaleString()}
//         </p>
//       </div>
//     </div>
//   );
// }

function StockHistoryPage() {
  const { t } = useTranslation();
  const [goodsList, setGoodsList] = useState<StockGoods[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selected, setSelected] = useState<StockGoods | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const products = await orpcClient.product.list({});
        if (cancelled) return;

        setGoodsList(
          sortStockGoods(buildStockGoods((products as any[]) ?? []))
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching stock history:", error);
        setGoodsList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return goodsList;

    return goodsList.filter((goods) =>
      [
        goods.name,
        goods.itemName,
        ...goods.locations.flatMap((location) => [
          location.warehouse,
          location.area,
          location.location,
        ]),
      ].some((value) => value?.toLowerCase().includes(keyword))
    );
  }, [goodsList, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const hasNext = currentPage < totalPages;

  const rangeFrom = (currentPage - 1) * pageSize + 1;
  const rangeTo = (currentPage - 1) * pageSize + pageRows.length;

  // const totals = useMemo(
  //   () => ({
  //     items: goodsList.length,
  //     in: goodsList.reduce((sum, goods) => sum + goods.in, 0),
  //     out: goodsList.reduce((sum, goods) => sum + goods.out, 0),
  //   }),
  //   [goodsList]
  // );

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("stockHistory.title") || "進出貨歷程"}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("stockHistory.subtitle") ||
              "點擊貨物即可查看每筆入庫與領料的時間與數量"}
          </div>
        </div>

        {/* <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SummaryCard
            icon={<IconBoxMultiple size={20} />}
            label={t("stockHistory.totalItems") || "貨物品項"}
            value={totals.items}
            tone="primary"
          />
          <SummaryCard
            icon={<IconArrowBarToDown size={20} />}
            label={t("stockHistory.totalIn") || "累計進貨"}
            value={totals.in}
            tone="success"
          />
          <SummaryCard
            icon={<IconArrowBarUp size={20} />}
            label={t("stockHistory.totalOut") || "累計出貨"}
            value={totals.out}
            tone="warning"
          />
        </div> */}

        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3.5">
            <div className="flex items-center gap-2">
              <IconPackage size={16} className="text-blue-600" />
              <h2 className="text-sm font-semibold text-gray-800">
                {t("stockHistory.goodsList") || "貨物清單"}
              </h2>
            </div>
            <TextInput
              className="w-full max-w-xs"
              leftSection={<IconSearch size={16} />}
              placeholder={
                t("stockHistory.searchPlaceholder") || "搜尋商品 / 品項 / 倉庫"
              }
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </div>

          <div className="hidden grid-cols-12 gap-4 border-b border-gray-200 px-5 py-2.5 text-xs font-medium text-gray-500 md:grid">
            <span className="col-span-4">
              {t("stockHistory.colProduct") || "商品"}
            </span>
            <span className="col-span-2">
              {t("stockHistory.colLocation") || "倉庫 / 儲位"}
            </span>
            <span className="col-span-2 text-right">
              {t("stockHistory.colIn") || "進貨"}
            </span>
            <span className="col-span-2 text-right">
              {t("stockHistory.colOut") || "出貨"}
            </span>
            <span className="col-span-2 text-right">
              {t("stockHistory.colStock") || "目前庫存"}
            </span>
          </div>

          <ul
            className={`divide-y divide-gray-200 transition-opacity ${
              loading ? "pointer-events-none opacity-50" : ""
            }`}
          >
            {pageRows.map((goods) => {
              const primary = goods.locations[0];
              const extra = goods.locations.length - 1;

              return (
                <li key={goods.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(goods)}
                    className="grid w-full grid-cols-1 items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-gray-50 md:grid-cols-12 md:gap-4"
                  >
                    <div className="flex items-center gap-3 md:col-span-4">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                        <IconPackage size={18} />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {goods.name}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {goods.itemName}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500 md:col-span-2">
                      <span className="md:hidden">
                        {t("stockHistory.colLocation") || "倉庫 / 儲位"}：
                      </span>
                      {primary ? (
                        <span className="truncate">
                          {primary.warehouse}
                          <span className="text-xs"> · {primary.location}</span>
                        </span>
                      ) : (
                        <span className="truncate">—</span>
                      )}
                      {extra > 0 && (
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-600">
                          +{extra} {t("stockHistory.locationUnit") || "儲位"}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between md:col-span-2 md:block md:text-right">
                      <span className="text-xs text-gray-500 md:hidden">
                        {t("stockHistory.colIn") || "進貨"}
                      </span>
                      <span className="text-sm font-medium tabular-nums text-emerald-600">
                        +{goods.in.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between md:col-span-2 md:block md:text-right">
                      <span className="text-xs text-gray-500 md:hidden">
                        {t("stockHistory.colOut") || "出貨"}
                      </span>
                      <span className="text-sm font-medium tabular-nums text-amber-600">
                        -{goods.out.toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2 md:col-span-2 md:justify-end">
                      <span className="text-xs text-gray-500 md:hidden">
                        {t("stockHistory.stockLabel") || "庫存"}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-semibold tabular-nums text-gray-800">
                          {goods.stock.toLocaleString()}
                          {goods.unit && (
                            <span className="ml-0.5 text-xs font-normal text-gray-500">
                              {goods.unit}
                            </span>
                          )}
                        </span>
                        <StockTag
                          value={goods.stock}
                          lowerLimit={goods.inventoryLowerLimit}
                        />
                        <IconChevronRight
                          size={16}
                          className="hidden text-gray-400 md:block"
                        />
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}

            {pageRows.length === 0 && (
              <li className="px-5 py-12 text-center text-sm text-gray-500">
                {loading
                  ? t("stockHistory.loading") || "載入中…"
                  : t("stockHistory.noResult") || "找不到符合的貨物"}
              </li>
            )}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{t("stockHistory.perPage") || "每頁筆數"}</span>
              <Select
                size="xs"
                className="w-[76px]"
                data={PAGE_SIZE_OPTIONS}
                value={String(pageSize)}
                allowDeselect={false}
                onChange={(value) => {
                  if (!value) return;
                  setPageSize(Number(value));
                  setPage(1);
                }}
                aria-label={t("stockHistory.perPage") || "每頁筆數"}
                data-testid="stock-history-page-size"
              />
              <span className="tabular-nums">
                {filtered.length > 0
                  ? `${rangeFrom}–${rangeTo} / ${filtered.length}`
                  : "0"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1 || loading}
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
              >
                <IconChevronLeft size={14} />
                {t("stockHistory.prevPage") || "上一頁"}
              </button>
              <span className="text-xs tabular-nums text-gray-600">
                {t("stockHistory.pageLabel") || "頁次"} {currentPage}
              </span>
              <button
                type="button"
                disabled={!hasNext || loading}
                onClick={() => setPage(currentPage + 1)}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
              >
                {t("stockHistory.nextPage") || "下一頁"}
                <IconChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </div>

      <StockMovementDrawer goods={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default StockHistoryPage;
